import axios from "axios";
import {reactive} from "vue";
import authHeader from "./authHeader";
import AxiosService from "./axiosService";

/**
 * Notifications Web Push.
 * Les appels passent par axios directement : AxiosService met en cache les POST,
 * ce qui empêcherait un second envoi identique.
 */
class AppPushService {
  constructor() {
    // État réactif utilisé par l'interface (bouton cloche)
    this.state = reactive({
      supported: false,
      permission: 'default', // 'default' | 'granted' | 'denied'
      subscribed: false,
      busy: false,
    });
  }

  url(target) {
    AxiosService.setEndPoint();
    return AxiosService.ENDPOINT + target;
  }

  isSupported() {
    return typeof window !== 'undefined'
      && 'serviceWorker' in navigator
      && 'PushManager' in window
      && 'Notification' in window;
  }

  /** Enregistrement du service worker actif (null en mode SPA / dev sans PWA) */
  async getRegistration() {
    if (!this.isSupported()) return null;
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return null;
    return navigator.serviceWorker.ready;
  }

  /** Met à jour l'état (supporté / permission / abonné) */
  async syncState() {
    const registration = await this.getRegistration();
    this.state.supported = !!registration;
    this.state.permission = this.isSupported() ? Notification.permission : 'denied';
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    this.state.subscribed = !!subscription && this.state.permission === 'granted';
    return this.state;
  }

  /** Active les notifications : permission → abonnement navigateur → enregistrement backend */
  async enable() {
    this.state.busy = true;
    try {
      const registration = await this.getRegistration();
      if (!registration) {
        throw new Error("Notifications indisponibles : application non installée en mode PWA");
      }

      const permission = await Notification.requestPermission();
      this.state.permission = permission;
      if (permission !== 'granted') {
        throw new Error("Notifications refusées par le navigateur");
      }

      const {data} = await axios.get(this.url('/appPush/publicKey'));
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(data.publicKey),
        });
      }

      await axios.post(this.url('/appPush/subscribe'), subscription.toJSON(), {headers: authHeader()});
      this.state.subscribed = true;
    } finally {
      this.state.busy = false;
    }
  }

  /** Désactive les notifications sur cet appareil */
  async disable() {
    this.state.busy = true;
    try {
      const registration = await this.getRegistration();
      const subscription = registration ? await registration.pushManager.getSubscription() : null;
      if (subscription) {
        await axios.post(this.url('/appPush/unsubscribe'), {endpoint: subscription.endpoint}, {headers: authHeader()});
        await subscription.unsubscribe();
      }
      this.state.subscribed = false;
    } finally {
      this.state.busy = false;
    }
  }

  /** Notification de test vers mes appareils */
  async sendTest() {
    const {data} = await axios.post(this.url('/appPush/test'), {}, {headers: authHeader()});
    return data;
  }

  /**
   * Envoi manuel (admin)
   * @param {{title: string, body: string, url?: string, userIds?: number[], all?: boolean}} message
   * @returns {Promise<{sent: number, failed: number, removed: number}>}
   */
  async send(message) {
    const {data} = await axios.post(this.url('/appPush/send'), message, {headers: authHeader()});
    return data;
  }

  /** Nombre d'appareils abonnés par utilisateur (admin) : { userId: count } */
  async stats() {
    const {data} = await axios.get(this.url('/appPush/stats'), {headers: authHeader()});
    return data.counts || {};
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
  }
}

export default new AppPushService();
